import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, UntypedFormArray } from '@angular/forms';
import { ApiService } from 'src/app/core/services/api.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-demands',
  templateUrl: './demands.component.html',
  styleUrls: ['./demands.component.scss'],
  standalone: false
})
export class DemandsComponent implements OnInit {
  breadCrumbItems!: Array<{}>;
  demandForm!: UntypedFormGroup;
  submitted = false;
  showForm = false;
  isEditMode = false;
  editId: any = null;

  // Data
  demands: any[] = [];
  items: any[] = [];
  suppliers: any[] = [];
  supplierItemsMap: { [key: number]: any[] } = {}; // Store items per supplier
  isLoading = false;
  totalItems = 0;

  // Pagination
  page: number = 1;
  pageSize: number = 10;

  // Priority options
  priorityOptions = ['Low', 'Medium', 'High', 'Urgent'];

  constructor(
    private fb: UntypedFormBuilder,
    private api: ApiService
  ) {}

  ngOnInit(): void {
    this.breadCrumbItems = [
      { label: 'Inventory' },
      { label: 'Demands', active: true }
    ];
    this.initializeForm();
    this.loadDemands();
    this.loadSuppliers();
  }

  initializeForm(): void {
    this.demandForm = this.fb.group({
      required_by_date: ['', Validators.required],
      priority: ['Medium', Validators.required],
      purpose: ['', Validators.required],
      remarks: [''],
      suppliers: this.fb.array([this.createSupplierFormGroup()])
    });
  }

  createSupplierFormGroup(): UntypedFormGroup {
    const group = this.fb.group({
      supplier_id: ['', Validators.required],
      items: this.fb.array([this.createItemFormGroup()])
    });

    // Listen to supplier changes
    group.get('supplier_id')?.valueChanges.subscribe(supplierId => {
      if (supplierId) {
        this.onSupplierChange(supplierId);
      }
    });

    return group;
  }

  createItemFormGroup(): UntypedFormGroup {
    return this.fb.group({
      item_id: ['', Validators.required],
      requested_quantity: [1, [Validators.required, Validators.min(1)]],
      // expected_delivery_date: ['', Validators.required],
      specifications: [''],
      remarks: ['']
    });
  }

  get f() { 
    return this.demandForm.controls; 
  }

  get demandSuppliers(): UntypedFormArray {
    return this.demandForm.get('suppliers') as UntypedFormArray;
  }

  getSupplierItems(supplierIndex: number): UntypedFormArray {
    return this.demandSuppliers.at(supplierIndex).get('items') as UntypedFormArray;
  }

  addSupplier(): void {
    this.demandSuppliers.push(this.createSupplierFormGroup());
  }

  removeSupplier(index: number): void {
    if (this.demandSuppliers.length > 1) {
      this.demandSuppliers.removeAt(index);
    }
  }

  addItem(supplierIndex: number): void {
    const items = this.getSupplierItems(supplierIndex);
    items.push(this.createItemFormGroup());
  }

  removeItem(supplierIndex: number, itemIndex: number): void {
    const items = this.getSupplierItems(supplierIndex);
    if (items.length > 1) {
      items.removeAt(itemIndex);
    }
  }

  toggleForm(): void {
    this.showForm = !this.showForm;
    if (!this.showForm) this.resetForm();
  }

  resetForm(): void {
    this.submitted = false;
    this.isEditMode = false;
    this.editId = null;
    this.demandForm.reset({
      priority: 'Medium'
    });
    // Reset suppliers array to single supplier with single item
    while (this.demandSuppliers.length > 1) {
      this.demandSuppliers.removeAt(1);
    }
    const firstSupplier = this.demandSuppliers.at(0) as UntypedFormGroup;
    const items = firstSupplier.get('items') as UntypedFormArray;
    while (items.length > 1) {
      items.removeAt(1);
    }
    firstSupplier.reset();
  }

  loadDemands(): void {
    this.isLoading = true;
    
    const params = `page=${this.page}&limit=${this.pageSize}&status=&priority=&organizational_unit_id=&from_date=&to_date=&search=`;
    
    this.api.get(`/demands?${params}`).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        if (res.success) {
          this.demands = res.data || [];
          // Handle meta object structure
          if (res.meta) {
            this.totalItems = Number(res.meta.total) || 0;
            this.pageSize = Number(res.meta.perPage) || this.pageSize;
            this.page = Number(res.meta.currentPage) || this.page;
          } else {
            this.totalItems = Number(res.total) || 0;
            this.pageSize = Number(res.perPage) || this.pageSize;
            this.page = Number(res.currentPage) || this.page;
          }
        } else {
          this.demands = [];
          this.totalItems = 0;
        }
      },
      error: () => {
        this.isLoading = false;
        this.demands = [];
        this.totalItems = 0;
      }
    });
  }

  loadItems(supplierId: number): void {
    // Check if items for this supplier are already loaded
    if (this.supplierItemsMap[supplierId]) {
      return;
    }

    this.api.get(`/items?supplier_id=${supplierId}`).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.supplierItemsMap[supplierId] = res.data || [];
        } else {
          this.supplierItemsMap[supplierId] = [];
        }
      },
      error: () => {
        this.supplierItemsMap[supplierId] = [];
      }
    });
  }

  onSupplierChange(supplierId: number): void {
    if (supplierId) {
      this.loadItems(supplierId);
    }
  }

  getItemsForSupplier(supplierId: number): any[] {
    return this.supplierItemsMap[supplierId] || [];
  }

  loadSuppliers(): void {
    this.api.get('/suppliers').subscribe({
      next: (res: any) => {
        if (res.success) {
          this.suppliers = res.data || [];
        } else {
          this.suppliers = [];
        }
      },
      error: () => {
        this.suppliers = [];
      }
    });
  }

  saveDemand(): void {
    this.submitted = true;

    if (this.demandForm.invalid) {
      Swal.fire('Error', 'Please fill all required fields', 'error');
      return;
    }

    const payload = this.demandForm.value;

    const apiCall = this.isEditMode
      ? this.api.put(`/demands/${this.editId}`, payload)
      : this.api.post('/demands', payload);

    apiCall.subscribe({
      next: (res: any) => {
        if (res.success) {
          Swal.fire('Success', res.message || (this.isEditMode ? 'Demand updated!' : 'Demand created!'), 'success');
          this.toggleForm();
          this.loadDemands();
        } else {
          Swal.fire('Error', res.message || 'Operation failed', 'error');
        }
      },
      error: (err) => {
        Swal.fire('Error', err.error?.message || 'Operation failed', 'error');
      }
    });
  }

  editDemand(demand: any): void {
    this.showForm = true;
    this.isEditMode = true;
    this.editId = demand.id;
    
    // Clear existing suppliers
    while (this.demandSuppliers.length > 0) {
      this.demandSuppliers.removeAt(0);
    }

    // Populate form
    this.demandForm.patchValue({
      required_by_date: demand.requiredByDate || demand.required_by_date,
      priority: demand.priority,
      purpose: demand.purpose,
      remarks: demand.remarks
    });

    // Add suppliers and items based on flat items array
    if (demand.items && demand.items.length > 0) {
      // Group items by supplier
      const supplierGroups: { [key: number]: any[] } = {};
      const itemsWithoutSupplier: any[] = [];
      
      demand.items.forEach((item: any) => {
        if (item.supplierId && item.supplier) {
          if (!supplierGroups[item.supplierId]) {
            supplierGroups[item.supplierId] = [];
          }
          supplierGroups[item.supplierId].push(item);
        } else {
          itemsWithoutSupplier.push(item);
        }
      });
      
      // Create supplier groups
      Object.keys(supplierGroups).forEach(supplierId => {
        const supplierGroup = this.createSupplierFormGroup();
        const items = supplierGroup.get('items') as UntypedFormArray;
        
        // Clear default item
        while (items.length > 0) {
          items.removeAt(0);
        }
        
        supplierGroup.patchValue({
          supplier_id: Number(supplierId)
        });
        
        // Add items for this supplier
        supplierGroups[Number(supplierId)].forEach((item: any) => {
          const itemGroup = this.createItemFormGroup();
          itemGroup.patchValue({
            item_id: item.itemId || item.item_id,
            requested_quantity: parseFloat(item.requestedQuantity || item.requested_quantity),
            // expected_delivery_date: item.expectedDeliveryDate || item.expected_delivery_date,
            specifications: item.specifications,
            remarks: item.remarks
          });
          items.push(itemGroup);
        });
        
        this.demandSuppliers.push(supplierGroup);
      });
      
      // Add items without supplier as separate group
      if (itemsWithoutSupplier.length > 0) {
        const supplierGroup = this.createSupplierFormGroup();
        const items = supplierGroup.get('items') as UntypedFormArray;
        
        // Clear default item
        while (items.length > 0) {
          items.removeAt(0);
        }
        
        itemsWithoutSupplier.forEach((item: any) => {
          const itemGroup = this.createItemFormGroup();
          itemGroup.patchValue({
            item_id: item.itemId || item.item_id,
            requested_quantity: parseFloat(item.requestedQuantity || item.requested_quantity),
            // expected_delivery_date: item.expectedDeliveryDate || item.expected_delivery_date,
            specifications: item.specifications,
            remarks: item.remarks
          });
          items.push(itemGroup);
        });
        
        this.demandSuppliers.push(supplierGroup);
      }
    } else {
      this.demandSuppliers.push(this.createSupplierFormGroup());
    }
  }

  viewDemand(demand: any): void {
    let itemsHtml = '';
    
    if (demand.items && demand.items.length > 0) {
      itemsHtml = '<div class="table-responsive mt-3"><table class="table table-sm table-bordered">';
      itemsHtml += '<thead class="table-light"><tr><th>#</th><th>Supplier</th><th>Item</th><th>Quantity</th><th>Unit</th><th>Specifications</th><th>Remarks</th></tr></thead><tbody>';
      
      demand.items.forEach((item: any, index: number) => {
        itemsHtml += `<tr>
          <td>${index + 1}</td>
          <td>${item.supplier?.name || 'Not assigned'}</td>
          <td>${item.item?.name || '-'}</td>
          <td>${item.requestedQuantity || '-'}</td>
          <td>${item.item?.unit?.symbol || '-'}</td>
          <td>${item.specifications || '-'}</td>
          <td>${item.remarks || '-'}</td>
        </tr>`;
      });
      
      itemsHtml += '</tbody></table></div>';
    } else {
      itemsHtml = '<p class="text-muted">No items found</p>';
    }

    Swal.fire({
      title: `<strong>Demand Details</strong>`,
      html: `
        <div class="text-start">
          <div class="row mb-2">
            <div class="col-6"><strong>Demand Number:</strong></div>
            <div class="col-6">${demand.demandNumber || '-'}</div>
          </div>
          <div class="row mb-2">
            <div class="col-6"><strong>Required By Date:</strong></div>
            <div class="col-6">${demand.requiredByDate ? new Date(demand.requiredByDate).toLocaleDateString() : '-'}</div>
          </div>
          <div class="row mb-2">
            <div class="col-6"><strong>Priority:</strong></div>
            <div class="col-6"><span class="badge ${this.getPriorityClass(demand.priority)}">${demand.priority || '-'}</span></div>
          </div>
          <div class="row mb-2">
            <div class="col-6"><strong>Status:</strong></div>
            <div class="col-6"><span class="badge ${this.getStatusClass(demand.status)}">${demand.status || '-'}</span></div>
          </div>
          <div class="row mb-2">
            <div class="col-6"><strong>Purpose:</strong></div>
            <div class="col-6">${demand.purpose || '-'}</div>
          </div>
          <div class="row mb-2">
            <div class="col-6"><strong>Organizational Unit:</strong></div>
            <div class="col-6">${demand.organizationalUnit?.name || '-'}</div>
          </div>
          ${demand.remarks ? `<div class="row mb-2">
            <div class="col-6"><strong>Remarks:</strong></div>
            <div class="col-6">${demand.remarks}</div>
          </div>` : ''}
          <div class="row mb-2">
            <div class="col-6"><strong>Created By:</strong></div>
            <div class="col-6">${demand.creator?.fullName || '-'}</div>
          </div>
          <div class="row mb-2">
            <div class="col-6"><strong>Demand Date:</strong></div>
            <div class="col-6">${demand.demandDate ? new Date(demand.demandDate).toLocaleDateString() : '-'}</div>
          </div>
          <hr>
          <h6 class="mt-3 mb-2">Items</h6>
          ${itemsHtml}
        </div>
      `,
      width: '800px',
      showCloseButton: true,
      confirmButtonText: 'Close'
    });
  }

  deleteDemand(id: number): void {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You will not be able to recover this demand!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!'
    }).then(result => {
      if (result.isConfirmed) {
        this.api.delete(`/demands/${id}`).subscribe({
          next: (res: any) => {
            if (res.success) {
              Swal.fire('Deleted!', res.message || 'Demand deleted successfully', 'success');
              this.loadDemands();
            }
          }
        });
      }
    });
  }

  changePage(page: number): void {
    this.page = page;
    this.loadDemands();
  }

  getMin(a: number, b: number): number {
    return Math.min(a, b);
  }

  getPriorityClass(priority: string): string {
    const classes: any = {
      'Urgent': 'bg-danger',
      'High': 'bg-warning',
      'Medium': 'bg-info',
      'Low': 'bg-secondary'
    };
    return classes[priority] || 'bg-secondary';
  }

  getStatusClass(status: string): string {
    const classes: any = {
      'Pending': 'bg-warning',
      'Approved': 'bg-success',
      'Rejected': 'bg-danger',
      'Completed': 'bg-success',
      'Cancelled': 'bg-secondary'
    };
    return classes[status] || 'bg-secondary';
  }

  getTotalItems(demand: any): number {
    if (!demand?.suppliers || demand.suppliers.length === 0) {
      return 0;
    }
    return demand.suppliers.reduce((total: number, supplier: any) => {
      return total + (supplier?.items?.length || 0);
    }, 0);
  }
}
